---
title: "Nice Meeting You at INTERFACE Boise"
keywords: "INTERFACE Boise, Gage Technologies, Boise IT partner, Idaho technology consulting, AI readiness, managed services, network security Idaho"
seo:
  title: "Nice Meeting You at INTERFACE Boise | Gage Technologies"
  description: "You met Gage at INTERFACE Boise. The short version of what we do: AI with governance, security that holds up under audit, and systems that fit a flat budget. Local team in the Treasure Valley."
  image: "/img/ai-overview/server-room.webp"
  twitter:
    card: "summary_large_image"
    title: "Nice Meeting You at INTERFACE Boise | Gage Technologies"
    description: "AI with governance, security that holds up under audit, and systems that fit a flat budget. Local team in the Treasure Valley."
    image: "/img/ai-overview/server-room.webp"
---

<PotsHero
  title="Nice Meeting You at INTERFACE Boise"
  description="You stopped by our booth or caught our session on August 20. This page is the short version of who Gage is and what we do, so you don't have to dig for it."
  ctaText="Start a Conversation"
  ctaHref="#contact"
  secondaryCtaText="What Greg Covered"
  secondaryCtaHref="#session"
/>

<Section id="session">
  <PotsEyebrow>The 11 AM Session</PotsEyebrow>
  ## What Greg Covered

  <p class="if-intro-center">Greg Hatch, our SVP of Strategy and Innovation, spent his hour on what it takes to move from a working demo to something that runs a real workflow. He opened with an uncomfortable number: RAND research finds that <a href="https://www.rand.org/pubs/research_reports/RRA2680-1.html" target="_blank">over 80% of AI projects fail</a> to deliver their intended value. The demo was never the hard part. Here's the rest of what he covered.</p>

  <PotsCards>
    <PotsCard
      icon="cpu"
      title="AI Is Not New to Us"
      description="Gage staff have been building AI since the IVR systems of the 1990s. The tools have changed. What makes a project succeed has not."
    />
    <PotsCard
      icon="warning"
      title="The Gap Is Organizational"
      description="Executives hear one story from vendors and IT inherits another. Token costs, data exposure, integration debt, and change management sink projects after the contract is signed."
    />
    <PotsCard
      icon="shield"
      title="Governance Before Features"
      description="Security, compliance, and human oversight come first. Especially in regulated work like healthcare, government, and finance."
    />
    <PotsCard
      icon="trending"
      title="Where It Works Today"
      description="QA automation, conversational self-service, documentation, fraud detection. Boring, measurable wins beat flashy pilots every time."
    />
  </PotsCards>

  <a class="if-more-link" href="/ai-overview">Read the full AI overview</a>
</Section>

<Section data-gray>
  <PotsEyebrow>Sound Familiar?</PotsEyebrow>
  ## The Conversations We Kept Having at Booth 212

  <p class="if-intro-center">Most people who stopped by described some version of the same three problems.</p>

  <PotsCards>
    <PotsCard
      icon="cpu"
      title="You're Expected to Do Something with AI"
      description="The board asks about it constantly. Nobody has written down where the data goes, what the rules are, or what success looks like. We help you build the roadmap before the purchase order."
    />
    <PotsCard
      icon="lock"
      title="Compliance Shapes Everything"
      description="HIPAA, CJIS, state security standards. Your environment has to hold up under audit and under real attack. We work in both worlds every day."
    />
    <PotsCard
      icon="server"
      title="Flat Budgets, Aging Systems"
      description="More locations, more users, the same money. We're vendor-agnostic, so our advice follows your outcomes, not a supplier's quota. Sometimes the answer is a redesign. Sometimes it's a contract renegotiation."
    />
  </PotsCards>
</Section>

<Section>
  <PotsEyebrow>Who We Are</PotsEyebrow>
  ## A Local Team with a Deep Bench

  <p class="if-intro-center">Gage is an outcome-based systems integrator. That's a fancy way of saying we're measured on whether your systems actually work, year after year. Our engineers live and work in the Treasure Valley, and we stay after the sale. Much of our business comes from referrals and long relationships.</p>

  <StatBar>
    <Stat value="20+" label="Years in business" />
    <Stat value="500+" label="Clients nationwide" />
    <Stat value="25+" label="Technology partners" />
    <Stat value="24/7" label="Monitoring and support" />
  </StatBar>
</Section>

<Section data-gray>
  <PotsEyebrow>Proof</PotsEyebrow>
  ## Organizations We Work With

  <p class="if-intro-center">A few of the teams Gage works with, from national retail to Idaho's own not-for-profit health system.</p>

  <div class="if-logo-row">
    <div class="if-logo-item">
      <img class="if-logo" src="/img/interface-boise/nordstrom.svg" alt="Nordstrom logo" />
      <span class="if-logo-caption">Nordstrom</span>
    </div>
    <div class="if-logo-item">
      <img class="if-logo" src="/img/interface-boise/hollandamerica.svg" alt="Holland America Line logo" />
      <span class="if-logo-caption">Holland America Line</span>
    </div>
    <div class="if-logo-item">
      <img class="if-logo" src="/img/interface-boise/stlukes.svg" alt="St. Luke's Health System logo" />
      <span class="if-logo-caption">St. Luke's Health System</span>
    </div>
  </div>
</Section>

<Section id="contact">
  <ContactForm>
    ## Start a<br/>Conversation
    <p class="if-contact-subcopy">Ask a question, kick around an idea, or take 20 minutes with one of our engineers to compare notes. No pitch, no obligation. If we're not the right fit, we'll tell you.</p>
  </ContactForm>
</Section>
